"use client";

import { type ReactNode, useContext, useId } from "react";
import { LOGO_PATHS } from "@/brand/logo-paths";
import { LOGO_VIEWS } from "@/brand/logo-views";
import { AppBrandContext } from "@/components/brand-provider/app-brand-provider";
import { cn } from "@/lib/utils";

const TEXT_SHIFT = "matrix(1,0,0,1,63.265993,-885.521238)";

const LETTER_SCALE = 103.30743;

/** The Saads logo. The wordmark is currentColor. Set --logo-ring / --logo-band on a parent for the white version; `mark` shows the infinity only; children are drawn on top. */
export function Logo({ variant = "full", className, children }: { variant?: keyof typeof LOGO_VIEWS; className?: string; children?: ReactNode }) {
  const gradient = `logo-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox={LOGO_VIEWS[variant]} role="img" aria-label="Saads" className={className} fillRule="evenodd">
      <defs>
        <linearGradient id={gradient} gradientUnits="userSpaceOnUse" gradientTransform="matrix(206.652675,0,0,98.179183,24.506548,70.321161)" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#662b83" />
          <stop offset="1" stopColor="#aa62da" />
        </linearGradient>
      </defs>
      <path d={LOGO_PATHS.ring} style={{ fill: `var(--logo-ring, url(#${gradient}))` }} />
      <path d={LOGO_PATHS.highlight} style={{ fill: "var(--logo-band, #fff)" }} />
      {variant === "full" && (
        <g transform={TEXT_SHIFT} fill="currentColor">
          {LOGO_PATHS.letters.map(({ x, d }) => (
            <path key={x} d={d} transform={`matrix(${LETTER_SCALE},0,0,${LETTER_SCALE},${x},992)`} />
          ))}
        </g>
      )}
      {children}
    </svg>
  );
}

/** The app's logo: the uploaded light and dark pictures switched by the dark class (no dark picture = the light one for both), or the bundled logo when none is uploaded. Pictures are always shown as images, never inlined. */
export function BrandLogo({ variant, className }: { variant?: "full" | "mark"; className?: string }) {
  const { logo, logoDark } = useContext(AppBrandContext);
  if (!logo) return <Logo variant={variant} className={className} />;
  const alt = "Saads";
  if (!logoDark) return <img src={logo} alt={alt} className={cn("object-contain", className)} />;
  return (
    <>
      <img src={logo} alt={alt} className={cn("object-contain dark:hidden", className)} />
      <img src={logoDark} alt={alt} className={cn("hidden object-contain dark:block", className)} />
    </>
  );
}
